import React, { ErrorInfo, FC, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { FinancialCalcResult } from '../api/selfApi'
import { makeChart, resetChart } from '../js/financialCalculatorChart'
import { AppState } from '../store'
import {
  RESTYPE_ADDITION,
  RESTYPE_INTEREST,
  RESTYPE_MONTHS,
  RESTYPE_TARGET,
  FinancialCalcResults,
} from '../store/modules/financialCalc'

const chartId = 'financialCalcChart'

export interface ErrorBoundaryProps {
  notifyError?: (error: Error, info: ErrorInfo) => void
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface FinancialCalcChartProps {
  select: string
  isResponsive: boolean
}

interface ChartSeries {
  type: string
  name: string
  color: string
  borderColor: string
  borderRadiusTopLeft?: number | string
  borderRadiusTopRight?: number | string
  data: { month: number; x: number; y: number }[]
  states: { hover: { color: string; borderColor: string } }
}

function makeChartSeries(result: FinancialCalcResult): ChartSeries[] | null {
  // 毎月の結果を本数に
  let graphStep = 1
  if (result.months <= 36) {
    // 3年以下なら毎月
    graphStep = 1
  } else if (result.months <= 144) {
    // 12年以下なら3ヵ月毎
    graphStep = 3
  } else if (result.months <= 216) {
    // 18年以下なら6ヵ月毎
    graphStep = 6
  } else {
    // 18年超なら毎年
    graphStep = 12
  }

  const lastMonthIdx = result.monthList.length - 1

  const monthListForChart = result.monthList.filter(
    (_v, idx) => (idx + 1) % graphStep === 0 || idx === lastMonthIdx
  )
  const principalListForChart = result.principalList.filter(
    (_v, idx) => (idx + 1) % graphStep === 0 || idx === lastMonthIdx
  )
  const profitListForChart = result.profitList.filter(
    (_v, idx) => (idx + 1) % graphStep === 0 || idx === lastMonthIdx
  )
  return result
    ? [
        {
          type: 'column',
          name: '運用収益',
          color: '#62a598',
          borderColor: '#62a598',
          borderRadiusTopLeft: '20%',
          borderRadiusTopRight: '20%',
          states: {
            hover: {
              color: '#f98197',
              borderColor: '#f98197',
            },
          },
          data: monthListForChart.map((month, idx) => {
            return {
              month: month,
              x: idx,
              y: profitListForChart[idx],
            }
          }),
        },
        {
          type: 'column',
          name: '投資元本',
          color: '#9ecec5',
          borderColor: '#9ecec5',
          states: {
            hover: {
              color: '#ffbeca',
              borderColor: '#ffbeca',
            },
          },
          data: monthListForChart.map((month, idx) => {
            return {
              month: month,
              x: idx,
              y: principalListForChart[idx],
            }
          }),
        },
      ]
    : null
}

function switchTabWithChart(
  selectTab: string,
  results: FinancialCalcResults,
  isResponsive: boolean
) {
  switch (selectTab) {
    case RESTYPE_TARGET:
      if (results.target) makeChart(chartId, makeChartSeries(results.target), isResponsive)
      else resetChart()
      break
    case RESTYPE_ADDITION:
      if (results.addition) makeChart(chartId, makeChartSeries(results.addition), isResponsive)
      else resetChart()
      break
    case RESTYPE_INTEREST:
      if (results.interest) makeChart(chartId, makeChartSeries(results.interest), isResponsive)
      else resetChart()
      break
    case RESTYPE_MONTHS:
      if (results.months) makeChart(chartId, makeChartSeries(results.months), isResponsive)
      else resetChart()
      break
    default:
      resetChart()
      break
  }
}

export const FinancialCalculatorChart: FC<FinancialCalcChartProps> = React.memo((props) => {
  const result = useSelector((state: AppState) => state.financialCalc.result)
  const detectionWidth = useRef(window.innerWidth)
  const responseCode = useSelector((state: AppState) => state.financialCalc.responseCode)

  useEffect(() => {
    function handleResize() {
      setTimeout(() => {
        const newWidth = window.innerWidth
        if (detectionWidth.current !== newWidth) {
          detectionWidth.current = newWidth
          switchTabWithChart(props.select, result, props.isResponsive)
        }
      }, 500)
    }
    if (responseCode === -4) {
      alert(
        '計算結果が上限を超えたため、評価額が目標額を達成できません。ご利用方法を確認してください。'
      )
      return window.removeEventListener('resize', () => handleResize())
    } else {
      switchTabWithChart(props.select, result, props.isResponsive)
      return window.removeEventListener('resize', () => handleResize())
    }
  }, [props.select, result, responseCode, props.isResponsive])
  return <div id={chartId} className="u-mt30px" />
})
